import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { compose, withProps } from 'recompose';

import { querySelector } from '../../../utils';
import queryMetaHandler from '../../../utils/queryMetaHandler';
import omitProps from '../../../utils/omitProps';

const TimesheetFragment = gql`
  fragment Timesheet on UserTimesheet {
    id
    key
    approvable
    missing_timesheets {
      iso8601
      display
    }
    unworked_per_diems {
      iso8601
      display
    }
    pending_per_diems {
      iso8601
      display
    }
    firstWeekApprovalRequired: first_week_approval_required
    user {
      id
      firstname
      guaranteed_eligible
      lastname
      fullname
      payroll_type
    }
    undertime {
      to_string
      total_seconds
    }
    overtime {
      to_string
    }
    start_date {
      iso8601
      display
    }
    state
    pto {
      to_string
    }
    total_hours {
      to_string
    }
    out_of_service {
      to_string
    }
    worked_hours {
        to_string
    }
    daily_time_entries {
      date {
        day_of_week
      }
      total_hours {
        to_string
      }
    }
    payPeriodWeek: pay_period_week {
      id
    }
    promptForGuaranteePay: prompt_for_guarantee_pay
    payPeriodSummary: pay_period_summary {
      userTimesheets: user_timesheets {
        id
        startDate: start_date {
          display
          iso8601
        }
        totalHours: total_hours {
          to_string
        }
      }
      undertimeDuration: undertime_duration {
        to_string
        total_seconds
      }
      totalDuration: total_duration {
        to_string
        total_seconds
      }
      hasGuaranteedTime: has_guaranteed_time
    }
  }
`;

export const GET_TIMESHEETS_FOR_PAYPERIOD = gql`
  query GetTimesheetsForPayPeriodQuery($supervisorId: Int, $approvalState: UserTimesheetStates) {
    all_timesheets: all_timesheets_for_pay_period(supervisor_id: $supervisorId, state: $approvalState) {
      edges {
        node {
          ...Timesheet
        }
      }
    }
  }
  ${TimesheetFragment}
`;

const marshallTimesheetData = ({ ownProps, data }) => {
  const timesheets = querySelector(data, 'all_timesheets.edges[*].node', []);
  return {
    ...ownProps,
    timesheets,
    data,
  };
};

const queryTimesheets = graphql(GET_TIMESHEETS_FOR_PAYPERIOD, {
  props: marshallTimesheetData,
});

export const queryTimesheetsGQL = compose(
  queryTimesheets,
  queryMetaHandler,
  omitProps(['data']),
);

export const GET_WEEKLY_TIMESHEETS_HOURS_QUERY = gql`
  query GetUsersWeeklyTimesheet($userId: Int!, $originDate: String!) {
    one_timesheet_for_one_user(user_id: $userId, week_of: $originDate) {
      ...Timesheet
    }
  }
  ${TimesheetFragment}
`;

const marshallWeeklyTimesheetData = ({ data, ownProps }) => {
  const results = querySelector(data, '$.one_timesheet_for_one_user', []);
  const userTimesheet = results[0];
  return {
    ...ownProps,
    data,
    userTimesheet,
  };
};

const queryUserWeeklyTimesheet = graphql(GET_WEEKLY_TIMESHEETS_HOURS_QUERY, {
  props: marshallWeeklyTimesheetData,
});

export const queryUserWeeklyTimesheetGQL = compose(
  queryUserWeeklyTimesheet,
  queryMetaHandler,
  omitProps(['data']),
);

const APPROVE_TIME_ENTRIES_QUERY = gql`
  mutation ApproveUserTimesheet($input: UserTimesheetApproveInput!) {
    user_timesheet_approve(input: $input) {
      id
    }
  }
`;
export const approveTimeEntriesMutation = (
  client,
  userId,
  startOfWeek,
  key,
  secondsToAdd,
  supervisorId,
) => {
  const variables = {
    input: {
      user_id: userId,
      start_of_week: startOfWeek,
      seconds_to_add: secondsToAdd,
      key,
    },
  };

  const options = {
    mutation: APPROVE_TIME_ENTRIES_QUERY,
    variables,
    refetchQueries: [
      {
        query: GET_TIMESHEETS_FOR_PAYPERIOD,
        variables: {
          supervisorId,
          approvalState: 'unapproved',
        },
      },
      {
        query: GET_TIMESHEETS_FOR_PAYPERIOD,
        variables: {
          supervisorId,
          approvalState: 'approved',
        },
      },
      {
        query: GET_WEEKLY_TIMESHEETS_HOURS_QUERY,
        variables: {
          userId,
          originDate: startOfWeek,
        },
      },
    ],
  };

  return client.mutate(options);
};

/* Details query in timesheet viewer */
export const WEEKLY_TIMESHEET_ENTRY_QUERY = gql`
  query GetWeeklyTimesheets($userId: Int!, $originDate: String!) {
    one_timesheet_for_one_user(user_id: $userId, week_of: $originDate) {
      id
      state
      start_date {
        iso8601
      }
      end_date {
        iso8601
      }
      time_entries {
        id
        paidPerDiem: paid_per_diem
        timezone
        start_time {
          iso8601
        }
        end_time {
          iso8601
        }
        key
        pto
        ptoType: pto_type
        notes
        date {
          iso8601
          display
          day_of_week
        }
        timesheet {
          id
          document {
            id
            submission_item {
              id
            }
          }
        }
        ticket_number {
          id
          number
          ticket {
            id
            state
            job {
              id
            }
          }
        }
        total_hours {
          to_string
        }
        equipment {
          id
          equipment_number
        }
        department {
          id
          name
        }
        district {
          id
          name
        }
        activity_display
      }
    }
  }
`;

const queryUserWeeklyTimeEntries = graphql(WEEKLY_TIMESHEET_ENTRY_QUERY);

const mapDataToProps = withProps((props) => {
  const { data } = props;
  const timeEntries = querySelector(data, '$.one_timesheet_for_one_user.time_entries[*]', []);
  const timesheet = data.one_timesheet_for_one_user;
  const weekStartDate = (timesheet) ? timesheet.start_date.iso8601 : '';
  const weekEndDate = (timesheet) ? timesheet.end_date.iso8601 : '';

  return {
    timeEntries,
    timesheet,
    weekStartDate,
    weekEndDate,
  };
});

export const queryUserWeeklyTimeEntriesGQL = compose(
  queryUserWeeklyTimeEntries,
  queryMetaHandler,
  mapDataToProps,
  omitProps(['data']),
);
